import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { hasuraClient } from '../utils/graphqlClient';
import { useNotification, useAuth } from './';
import { sendWelcomeEmail } from '../utils/emailNotification';
import {
  GET_COMPANIES_FOR_USER_FORM_QUERY,
  GET_ALL_TEAMS_QUERY,
  GET_TEAMS_BY_COMPANY_QUERY,
  GET_TITLES_QUERY,
} from '../graphql';
import { useDynamicEntity } from './useDynamicEntity';
import { formatDateForHasura } from '../utils/dateUtils';

export const useUsers = ({ paginated = false } = {}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { user } = useAuth();

  const [companies, setCompanies] = useState([]);
  const [titles, setTitles] = useState([]);
  const [teams, setTeams] = useState([]);

  const isRootUser = user?.role === 'ROOT';
  const userCompanyId = user?.companyId;

  const dynamicHook = useDynamicEntity({
    entityType: 'users',
    gqlEntityKey: 'users',
    queryName: 'GET_ALL_USERS_QUERY',
    createMutation: 'CREATE_USER_MUTATION',
    updateMutation: 'UPDATE_USER_MUTATION',
    deleteMutation: 'DELETE_USER_MUTATION',
    // Users pattern configuration
    mutationPattern: 'users',
    createVariableName: 'input',
    updateVariableName: 'input',
    serverPagination: paginated,
    aggregateKey: paginated ? 'users_aggregate' : null,
    transformData: user => ({
      id: user.id,
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      phoneNumber: user.phone_number,
      workPhoneNumber: user.work_phone_number,
      isLoanRequestManager: user.is_loan_request_manager,
      isExpenseManager: user.is_expense_manager,
      managesCollections: user.manages_collections,
      canViewCommunicationReport: user.can_view_communication_report === true,
      birthDate: user.user_birthday ? new Date(user.user_birthday) : null,
      address: user.address,
      role: user.role,
      teamId: user.team_id,
      teamName: user.team?.name,
      workStartDate: user.work_start_date
        ? new Date(user.work_start_date)
        : null,
      workEndDate: user.work_end_date ? new Date(user.work_end_date) : null,
      annualLeaveBalance: user.annual_leave_balance ?? 0,
      companyId: user.company_id,
      titleId: user.title_id,
      createdAt: user.created_at,
      updatedAt: user.updated_at,
      companyName: user.company?.name,
      isExpenseManager: user.is_expense_manager,
      titleName: user.title?.title_name,
      firmCount: (user.user_firms ?? []).length,
      teamTags: user.team?.team_tags,
      groupTags: user.group?.group_tags,
    }),
    transformFormData: formData => {
      let companyId;
      if (isRootUser) {
        companyId = formData.companyId || formData.company_id;
        if (!companyId) {
          throw new Error('ROOT kullanıcıları için şirket seçimi zorunludur');
        }
      } else {
        companyId = userCompanyId;
        if (!companyId) {
          throw new Error('Kullanıcının şirket bilgisi bulunamadı');
        }
      }

      // can_view_communication_report Hasura tarafinda yalnizca ROOT/DIRECTOR
      // tarafindan guncellenebilir; diger rollerin payload'a koymasi
      // permission error firlatir. Bu yuzden sadece yetkili rollerde gondeririz.
      const role = user?.role;
      const canEditCommReport = role === 'ROOT' || role === 'DIRECTOR';

      // Annual leave balance will be calculated by backend on create
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password,
        phone_number: formData.phoneNumber,
        work_phone_number: formData.workPhoneNumber || null,
        is_expense_manager: formData.isExpenseManager === true,
        is_loan_request_manager: formData.isLoanRequestManager === true,
        manages_collections: formData.managesCollections === true,
        user_birthday: formatDateForHasura(formData.birthDate),
        address: formData.address || null,
        role: formData.role,
        team_id: formData.teamId || null,
        work_start_date: formatDateForHasura(formData.workStartDate),
        work_end_date: formatDateForHasura(formData.workEndDate),
        title_id: formData.titleId || null,
        company_id: companyId,
      };

      if (canEditCommReport) {
        payload.can_view_communication_report =
          formData.canViewCommunicationReport === true;
      }

      return payload;
    },
    customWhereCondition: isRootUser
      ? {}
      : { company_id: { _eq: userCompanyId } },
  });

  const fetchCompanies = useCallback(async () => {
    if (!isRootUser) return;

    try {
      const data = await hasuraClient.query(GET_COMPANIES_FOR_USER_FORM_QUERY);
      setCompanies(data.companies);
    } catch (error) {
      console.error('Error fetching companies:', error);
      showNotification(t('companies.messages.fetchError'), 'error');
    }
  }, [isRootUser, t, showNotification]);

  const fetchTitles = useCallback(async () => {
    try {
      const data = await hasuraClient.query(GET_TITLES_QUERY);
      setTitles(data.titles);
    } catch (error) {
      console.error('Error fetching titles:', error);
      showNotification(t('titles.messages.fetchError'), 'error');
    }
  }, [t, showNotification]);

  const fetchTeams = useCallback(async () => {
    try {
      let data;
      if (isRootUser) {
        data = await hasuraClient.query(GET_ALL_TEAMS_QUERY);
      } else {
        data = await hasuraClient.query(GET_TEAMS_BY_COMPANY_QUERY, {
          where: { company_id: { _eq: userCompanyId } },
        });
      }
      setTeams(data.teams);
    } catch (error) {
      console.error('Error fetching teams:', error);
      showNotification(t('teams.messages.fetchError'), 'error');
    }
  }, [isRootUser, userCompanyId, t, showNotification]);

  const handleFormSubmit = useCallback(
    async (formData, editingId = null) => {
      try {
        const result = await dynamicHook.handleFormSubmit(formData, editingId);

        if (result.success && !editingId) {
          // Send welcome email for new users
          try {
            await sendWelcomeEmail({
              email: formData.email,
              firstName: formData.firstName,
              lastName: formData.lastName,
            });
          } catch (emailError) {
            console.error('Error sending welcome email:', emailError);
          }
        }

        return result;
      } catch (error) {
        return { success: false, error };
      }
    },
    [dynamicHook]
  );

  useEffect(() => {
    fetchCompanies();
    fetchTitles();
    fetchTeams();
  }, [user?.id, user?.role, user?.companyId]);

  return {
    ...dynamicHook,
    companies,
    titles,
    teams,
    isRootUser,
    handleFormSubmit,
  };
};
